import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Archive, Plus, Eye, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Collections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/collections`);
      setCollections(response.data);
    } catch (error) {
      console.error('Failed to fetch collections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCollection = async () => {
    if (!name || !description) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(
        `${API}/collections?name=${encodeURIComponent(name)}&description=${encodeURIComponent(description)}&is_public=${isPublic}`,
        {},
        { withCredentials: true }
      );
      toast.success('Collection created successfully!');
      setShowCreateModal(false);
      setName('');
      setDescription('');
      fetchCollections();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to create a collection');
        const redirectUrl = `${window.location.origin}/auth-callback`;
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      } else {
        toast.error('Failed to create collection');
      }
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="collections-page">
      <Navbar />
      
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-12">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="collections-title">
                Collector <span className="accent-red">Showcases</span>
              </h1>
              <p className="text-gray-400" data-testid="collections-description">
                Connect with a vibrant community of fellow enthusiasts and dive into an ever-growing showcase of rare artifacts and exclusive pop culture 
                collectibles—each collection reflecting the creativity, diversity, and personal passion of collectors from all across India.
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center space-x-2"
              data-testid="create-collection-btn"
            >
              <Plus size={20} />
              <span>Create Collection</span>
            </button>
          </div>

          {/* Collections Grid */}
          {loading ? (
            <div className="text-center py-20" data-testid="loading">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600 mx-auto mb-4"></div>
              <p className="text-gray-400">Loading collections...</p>
            </div>
          ) : collections.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="collections-grid">
              {collections.map(collection => (
                <div
                  key={collection.id}
                  className="product-card card-hover"
                  data-testid={`collection-${collection.id}`}
                >
                  <div className="relative h-48 bg-gradient-to-br from-red-900/30 to-purple-900/30 flex items-center justify-center">
                    <Archive size={64} className="text-red-500 opacity-50" />
                    {!collection.is_public && (
                      <div className="absolute top-4 right-4 bg-yellow-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                        <Lock size={14} />
                        <span>Private</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{collection.name}</h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">{collection.description}</p>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        {collection.user?.picture ? (
                          <img src={collection.user.picture} alt={collection.user.name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-xs font-bold">
                            {collection.user?.name?.charAt(0) || 'A'}
                          </div>
                        )}
                        <span className="text-sm text-gray-400">{collection.user?.name || 'Anonymous'}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-sm text-gray-400">
                        <Eye size={16} />
                        <span>{collection.items?.length || 0} items</span>
                      </div>
                    </div>

                    <button className="w-full btn-secondary py-2" data-testid={`view-collection-${collection.id}`}>
                      View Collection
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20" data-testid="no-collections">
              <Archive className="mx-auto mb-6 text-gray-600" size={80} />
              <h2 className="text-2xl font-bold mb-4">No collections yet</h2>
              <p className="text-gray-400 mb-8">Start showcasing your amazing collection!</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                Create Collection
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Collection Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)} data-testid="create-modal">
          <div className="bg-gray-900 rounded-2xl p-8 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-3xl font-bold mb-6" data-testid="modal-title">Create Collection</h2>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold mb-2">Collection Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Marvel Collection"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  data-testid="name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell us about your collection..."
                  rows="4"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500 resize-none"
                  data-testid="description-input"
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="public"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="w-5 h-5 accent-red-600"
                  data-testid="public-checkbox"
                />
                <label htmlFor="public" className="text-sm">
                  Make this collection public (others can view it)
                </label>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 btn-secondary"
                data-testid="modal-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCollection}
                className="flex-1 btn-primary"
                data-testid="modal-submit-btn"
              >
                Create Collection
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Collections;